// The server-held session (CASA 2.3.2, 2026-09).
//
// The Supabase session cookie is HttpOnly: no page script can read it, so an
// XSS can never lift the refresh token, the one secret that mints new sessions
// for weeks. The browser still needs an access token (API calls are Bearer,
// which is also what keeps them CSRF-proof), so it holds one, in memory only,
// for the life of the page.
//
// supabase-js stays the browser's session machine: onAuthStateChange,
// getSession, auto-refresh and signOut all behave exactly as before, and none
// of their ~25 callers change. Two seams make that possible:
//
//   1. STORAGE (createServerHeldStorage): memory only. The first read asks our
//      own origin for the session (GET /auth/session), which answers from the
//      cookie with the refresh token replaced by a placeholder.
//   2. TOKEN GRANTS (routeTokenGrantsToOrigin): supabase-js's calls to
//      Supabase's token endpoint (refresh, ID-token sign-in) go to our origin
//      instead (POST /auth/token). The server swaps the placeholder for the
//      real refresh token from the cookie, writes the rotated session back as
//      HttpOnly, and answers in Supabase's own response shape, redacted again.
//
// So the refresh token only ever travels between the cookie jar and our
// server. It never reaches page memory.

import { AUTH_REQUEST_TIMEOUT_MS } from "@/lib/supabase/bounded-auth-fetch";

/** What page memory holds in place of the refresh token. Non-empty, because supabase-js refuses to refresh without one. */
export const SERVER_HELD_REFRESH_TOKEN = "server-held";

/**
 * Stamped by /auth/session, /auth/token and /auth/signout on every answer they
 * MEAN, success or refusal. supabase-js destroys the session on any refresh
 * failure outside 502/503/504, so a Vercel crash page (500) or a proxy's 404
 * must never read as "your session is invalid". Only a stamped answer can sign
 * someone out. Anything else is treated as the network being down.
 */
export const AUTH_VERDICT_HEADER = "x-vestra-auth-verdict";

/**
 * A session as the page may hold it: the refresh token swapped for the
 * placeholder, and the Google provider tokens dropped (the page has no use
 * for them, and the provider refresh token is a long-lived Google secret).
 */
export function redactSession(
  session: Record<string, unknown>
): Record<string, unknown> {
  const redacted: Record<string, unknown> = {
    ...session,
    refresh_token: SERVER_HELD_REFRESH_TOKEN,
  };
  delete redacted.provider_token;
  delete redacted.provider_refresh_token;
  return redacted;
}

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

/** The async storage contract supabase-js accepts for `auth.storage`. */
export interface AsyncStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/** Backoff between hydration attempts: 250 ms doubling, capped at 5 s. */
export function hydrationDelay(attempt: number): number {
  return Math.min(250 * 2 ** attempt, 5000);
}

/**
 * Sleep, cut short the moment the browser reports it is back online. Holds on
 * to the window it started with: the timer can outlive the global (a test
 * environment torn down mid-sleep), and must not reach for one that is gone.
 */
function sleepUntilOnline(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const target = typeof window === "undefined" ? null : window;
    const done = () => {
      clearTimeout(timer);
      target?.removeEventListener("online", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    target?.addEventListener("online", done);
  });
}

/**
 * How long the page may go without learning its session before it says so.
 * Matches AuthGuard's own gate deadline: well above a normal answer (~200 ms),
 * and the point past which a person staring at the curtain deserves a reason.
 */
export const HYDRATION_STALL_MS = 8000;

// The page has one session, so "we cannot reach it" is page-wide: AuthContext
// listens here and AuthGuard turns it into the trouble screen, which lifts
// itself the moment the session arrives. Without it a dead /auth/session left
// the curtain down forever with no word (review of #3938).
let hydrationStalled = false;
const stallListeners = new Set<() => void>();

/** Called once the page has gone HYDRATION_STALL_MS without its session (immediately, if it already has). */
export function onSessionUnreachable(listener: () => void): () => void {
  if (hydrationStalled) listener();
  stallListeners.add(listener);
  return () => {
    stallListeners.delete(listener);
  };
}

function reportStall(): void {
  hydrationStalled = true;
  for (const listener of stallListeners) listener();
}

/**
 * What page memory may hold as the session: always the redacted form. Our
 * routes already redact; this makes storage fail closed if any other
 * supabase-js path (verifyOtp, signUp, MFA…) ever hands the page a session
 * straight from Supabase.
 */
function heldBack(value: string): string {
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed && typeof parsed === "object" && "refresh_token" in parsed) {
      return JSON.stringify(redactSession(parsed as Record<string, unknown>));
    }
  } catch {
    // not a session: store as given
  }
  return value;
}

export function createServerHeldStorage({
  fetch,
  sessionKey,
  sleep = sleepUntilOnline,
  isBrowser = () => typeof window !== "undefined",
  now = Date.now,
  onStalled = reportStall,
}: {
  fetch: FetchLike;
  /** Supabase's storage key for the session: `sb-<project-ref>-auth-token`. */
  sessionKey: string;
  sleep?: (ms: number) => Promise<void>;
  isBrowser?: () => boolean;
  now?: () => number;
  onStalled?: () => void;
}): AsyncStorage {
  const memory = new Map<string, string>();
  let hydration: Promise<void> | null = null;

  // Keeps asking until the server gives a real answer. Resolving "no session"
  // on a network blip would sign a signed-in person out (the app treats a null
  // INITIAL_SESSION as signed out), so a dead network waits, and wakes the
  // moment the browser comes back online. Each attempt carries its own
  // deadline, so a half-open socket cannot hang it (see bounded-auth-fetch).
  async function hydrate(): Promise<void> {
    const startedAt = now();
    let stallReported = false;
    for (let attempt = 0; ; attempt++) {
      try {
        // Same URL, mode and credentials as the <link rel="preload"> in the
        // root layout, so the first attempt picks up the preloaded answer.
        // No `cache` override: that would miss the preload, and the response
        // is no-store anyway.
        const res = await fetch("/auth/session", {
          credentials: "same-origin",
          signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS),
        });
        if (res.headers.get(AUTH_VERDICT_HEADER) === "1") {
          const body = (await res.json()) as { session?: unknown };
          if (body.session) {
            memory.set(sessionKey, JSON.stringify(body.session));
          }
          return;
        }
      } catch {
        // network error or deadline: retry below
      }
      if (!stallReported && now() - startedAt >= HYDRATION_STALL_MS) {
        stallReported = true;
        onStalled();
      }
      await sleep(hydrationDelay(attempt));
      // No page left to serve (it was torn down while we waited): stop.
      if (!isBrowser()) return;
    }
  }

  // The cookie is HttpOnly, so the page cannot delete it. Ask the server to.
  // Never throws: supabase-js awaits this inside sign-out, and a throw there
  // would abort the sign-out before SIGNED_OUT is announced.
  async function endServerSession(): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch("/auth/signout", {
          method: "POST",
          credentials: "same-origin",
          keepalive: true,
        });
        if (res.ok) return;
      } catch {
        // retry once, then give up: a dead session's next refresh is refused
        // by the server, which clears the cookie itself
      }
    }
  }

  return {
    async getItem(key) {
      if (key === sessionKey && isBrowser()) {
        hydration ??= hydrate();
        await hydration;
      }
      return memory.get(key) ?? null;
    },
    async setItem(key, value) {
      memory.set(key, key === sessionKey ? heldBack(value) : value);
    },
    async removeItem(key) {
      memory.delete(key);
      if (key === sessionKey && isBrowser()) await endServerSession();
    },
  };
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * Send supabase-js's token-endpoint calls (every grant: refresh, ID token,
 * anything added later) to our own /auth/token instead of Supabase, and trust
 * only a stamped answer. An unstamped one becomes a 503, which supabase-js
 * retries and never treats as a reason to sign out.
 */
export function routeTokenGrantsToOrigin(
  baseFetch: FetchLike,
  supabaseUrl: string
): FetchLike {
  const tokenEndpoint = new URL("/auth/v1/token", supabaseUrl).href;
  return async (input, init) => {
    let url: URL;
    try {
      url = new URL(requestUrl(input));
    } catch {
      return baseFetch(input, init);
    }
    if (`${url.origin}${url.pathname}` !== tokenEndpoint) {
      return baseFetch(input, init);
    }
    const res = await baseFetch(`/auth/token${url.search}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: init?.body,
      credentials: "same-origin",
      signal: init?.signal,
    });
    if (res.headers.get(AUTH_VERDICT_HEADER) === "1") return res;
    return new Response(null, {
      status: 503,
      statusText: "Session service unavailable",
    });
  };
}
